import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Checkbox,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";

interface FormField {
  enabled: boolean;
  required: boolean;
  label: string;
  type: string;
  placeholder?: string;
  options?: string[];
}

interface FormConfig {
  fields: {
    [key: string]: FormField;
  };
}

interface FormConfigurationEditorProps {
  formConfig: FormConfig;
  onChange: (config: FormConfig) => void;
}

const DEFAULT_FIELDS = {
  name: {
    enabled: true,
    required: true,
    label: "Full Name",
    type: "text",
    placeholder: "Enter your full name",
    description: "Customer's full name as per ID"
  },
  email: {
    enabled: true,
    required: true,
    label: "Email Address",
    type: "email",
    placeholder: "Enter your email address",
    description: "Primary contact email (always required)"
  },
  phone: {
    enabled: true,
    required: true,
    label: "Phone Number",
    type: "tel",
    placeholder: "Enter your phone number",
    description: "Mobile phone number for contact"
  },
  gender: {
    enabled: false,
    required: false,
    label: "Gender",
    type: "select",
    options: ["Male", "Female", "Other"],
    description: "Gender identification"
  },
  identity_number: {
    enabled: false,
    required: false,
    label: "Identity Number",
    type: "text",
    placeholder: "Enter your identity number (KTP/Passport)",
    description: "Government-issued identity number"
  },
  date_of_birth: {
    enabled: false,
    required: false,
    label: "Date of Birth",
    type: "date",
    placeholder: "Select your date of birth",
    description: "Customer's date of birth"
  },
  address: {
    enabled: false,
    required: false,
    label: "Address",
    type: "textarea",
    placeholder: "Enter your full address",
    description: "Full residential address"
  },
  emergency_contact: {
    enabled: false,
    required: false,
    label: "Emergency Contact",
    type: "text",
    placeholder: "Emergency contact name and phone",
    description: "Emergency contact information"
  },
  company: {
    enabled: false,
    required: false,
    label: "Company/Organization",
    type: "text",
    placeholder: "Enter your company or organization name",
    description: "Business or organization affiliation"
  },
  dietary_requirements: {
    enabled: false,
    required: false,
    label: "Dietary Requirements",
    type: "textarea",
    placeholder: "Any dietary restrictions or special requirements",
    description: "Special dietary needs or allergies"
  },
  special_needs: {
    enabled: false,
    required: false,
    label: "Special Needs",
    type: "textarea",
    placeholder: "Any accessibility requirements or special needs",
    description: "Accessibility or special assistance needs"
  }
};

const FormConfigurationEditor: React.FC<FormConfigurationEditorProps> = ({
  formConfig,
  onChange,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleFieldToggle = (fieldKey: string, enabled: boolean) => {
    if (fieldKey === "email" && !enabled) {
      // Email cannot be disabled
      return;
    }

    const updatedConfig = {
      ...formConfig,
      fields: {
        ...formConfig.fields,
        [fieldKey]: {
          ...formConfig.fields[fieldKey],
          enabled,
          required: enabled ? formConfig.fields[fieldKey]?.required || false : false,
        },
      },
    };
    onChange(updatedConfig);
  };

  const handleRequiredToggle = (fieldKey: string, required: boolean) => {
    if (fieldKey === "email") {
      // Email is always required
      return;
    }

    const updatedConfig = {
      ...formConfig,
      fields: {
        ...formConfig.fields,
        [fieldKey]: {
          ...formConfig.fields[fieldKey],
          required,
        },
      },
    };
    onChange(updatedConfig);
  };

  const getFieldStatus = (fieldKey: string) => {
    const field = formConfig.fields[fieldKey];
    if (!field?.enabled) return "disabled";
    if (field.required) return "required";
    return "optional";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "required": return "success";
      case "optional": return "info";
      case "disabled": return "default";
      default: return "default";
    }
  };

  const enabledCount = Object.values(formConfig.fields).filter(field => field?.enabled).length;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" gutterBottom>
          Checkout Form Configuration
        </Typography>
        <Button variant="outlined" onClick={() => setPreviewOpen(true)}>
          Preview Form ({enabledCount} fields)
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Configure which information to collect from customers during checkout. 
        Email is always required and cannot be disabled.
      </Alert>

      <Grid container spacing={2}>
        {Object.entries(DEFAULT_FIELDS).map(([fieldKey, defaultField]) => {
          const currentField = formConfig.fields[fieldKey] || defaultField;
          const status = getFieldStatus(fieldKey);
          const isEmailField = fieldKey === "email";

          return (
            <Grid item xs={12} sm={6} md={4} key={fieldKey}>
              <Card 
                variant="outlined" 
                sx={{ 
                  height: "100%",
                  opacity: status === "disabled" ? 0.6 : 1,
                  transition: "opacity 0.2s"
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {defaultField.label}
                    </Typography>
                    <Chip 
                      label={status} 
                      size="small" 
                      color={getStatusColor(status) as any}
                      variant={status === "disabled" ? "outlined" : "filled"}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {(defaultField as any).description}
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={currentField.enabled}
                          onChange={(e) => handleFieldToggle(fieldKey, e.target.checked)}
                          disabled={isEmailField}
                        />
                      }
                      label={`Show this field${isEmailField ? " (always shown)" : ""}`}
                    />
                    
                    {currentField.enabled && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentField.required}
                            onChange={(e) => handleRequiredToggle(fieldKey, e.target.checked)}
                            disabled={isEmailField}
                          />
                        }
                        label={`Make required${isEmailField ? " (always required)" : ""}`}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Checkout Form Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is how the checkout form will appear to customers:
          </Typography>
          
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Buyer Information
            </Typography>
            
            {Object.entries(formConfig.fields)
              .filter(([_, field]) => field?.enabled)
              .map(([fieldKey, field]) => (
                <Box key={fieldKey} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {field.label} {field.required && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  <Box 
                    sx={{ 
                      border: 1, 
                      borderColor: "divider", 
                      borderRadius: 1, 
                      p: 1, 
                      mt: 0.5,
                      color: "text.secondary",
                      fontStyle: "italic"
                    }}
                  >
                    {field.placeholder || `${field.label} input field`}
                  </Box>
                </Box>
              ))}
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              + Newsletter subscription and Terms & Conditions checkboxes will also appear
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormConfigurationEditor;
